import React, { useState, useRef, useEffect } from 'react';
import { Eye, ChevronDown, CheckCircle2, Archive, Sparkles } from 'lucide-react';
import { useUIOptional } from '../../../contexts/UIContext';

interface SubHeaderVisibilityDropdownProps {
  showCompleted?: boolean;
  setShowCompleted?: (val: boolean) => void;
  showArchived?: boolean;
  setShowArchived?: (val: boolean) => void;
  showTaskDetails?: boolean;
  setShowTaskDetails?: (val: boolean) => void;
  uiMode?: 'standard' | 'expert';
  setUiMode?: (val: 'standard' | 'expert') => void;
  onOpenAnotherDropdown?: () => void;
}

export const SubHeaderVisibilityDropdown: React.FC<SubHeaderVisibilityDropdownProps> = ({
  showCompleted = true,
  setShowCompleted,
  showArchived = false,
  setShowArchived,
  showTaskDetails = true,
  setShowTaskDetails,
  uiMode: propUiMode,
  setUiMode: propSetUiMode,
  onOpenAnotherDropdown
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const uiCtx = useUIOptional();
  const effectiveUiMode = propUiMode ?? uiCtx?.uiMode ?? 'standard';
  const effectiveSetUiMode = propSetUiMode ?? uiCtx?.setUiMode;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  if (!setShowCompleted && !setShowArchived && !effectiveSetUiMode) return null;

  const isCustomState = showCompleted === false || showArchived === true || showTaskDetails === false || effectiveUiMode === 'expert';

  return (
    <div className="relative h-[34px] shrink-0" ref={dropdownRef}>
      <div className="flex bg-slate-100 dark:bg-slate-800 p-0.5 rounded-xl border border-slate-200/50 dark:border-slate-800 h-[34px] items-center">
        <button 
          type="button"
          data-testid="visibility-dropdown-trigger"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label="Sichtbarkeit und Anzeigeoptionen"
          onClick={() => {
            setIsOpen(!isOpen);
            onOpenAnotherDropdown?.();
          }}
          className={`px-3 h-full rounded-lg flex items-center justify-center gap-1.5 transition-all text-[10px] font-bold uppercase tracking-widest ${
            isOpen || isCustomState
              ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-black/5 font-black' 
              : 'text-slate-400 hover:text-slate-500'
          }`}
          title="Sichtbarkeit & Anzeigeoptionen"
        >
          <Eye size={14} />
          <span>Anzeige</span>
          <ChevronDown size={10} className={`transition-transform duration-300 ${isOpen ? 'rotate-180 text-indigo-500' : 'group-hover:text-slate-500'}`} />
        </button>
      </div>

      {isOpen && (
        <div
          role="menu"
          aria-label="Anzeigeoptionen"
          className="absolute right-0 top-full mt-2 w-60 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] z-[100] p-2 flex flex-col gap-1.5 animate-in fade-in zoom-in-95 duration-200 origin-top-right"
        >
          {/* Title */}
          <div className="px-3 py-1.5 text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-850/50 mb-1">
            Anzeigeoptionen
          </div>

          {/* Toggle 1: Completed Tasks */}
          {setShowCompleted && (
            <button
              type="button"
              aria-pressed={showCompleted}
              aria-label="Erledigte Aufgaben anzeigen"
              onClick={() => setShowCompleted(!showCompleted)}
              title="Zeigt oder blendet Aufgaben ein, die bereits als erledigt markiert wurden"
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left transition-all duration-200 group/item border ${
                showCompleted 
                  ? 'hover:bg-slate-50 dark:hover:bg-slate-900 border-transparent text-slate-700 dark:text-slate-300' 
                  : 'bg-indigo-50/50 border-indigo-100 dark:bg-indigo-900/20 dark:border-indigo-900/50 text-indigo-600 dark:text-indigo-400 font-bold'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <CheckCircle2 size={14} className={showCompleted ? 'text-slate-400 group-hover/item:text-indigo-600' : 'text-indigo-600'} />
                <span className="text-[10px] font-bold uppercase tracking-wider">Erledigte</span>
              </div>
              
              {/* Premium Toggle Switch / Pill indicator */}
              <div className={`w-8 h-4 rounded-full transition-all relative ${showCompleted ? 'bg-indigo-600' : 'bg-slate-350 dark:bg-slate-700'}`}>
                <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${showCompleted ? 'left-4.5' : 'left-0.5'}`} />
              </div>
            </button>
          )}

          {/* Toggle 2: Archived Tasks */}
          {setShowArchived && (
            <button
              type="button"
              aria-pressed={showArchived}
              aria-label="Archivierte Aufgaben anzeigen"
              onClick={() => setShowArchived(!showArchived)}
              title="Zeigt oder blendet archivierte Aufgaben ein"
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left transition-all duration-200 group/item border ${
                showArchived 
                  ? 'bg-indigo-50/50 border-indigo-100 dark:bg-indigo-900/20 dark:border-indigo-900/50 text-indigo-600 dark:text-indigo-400 font-bold' 
                  : 'hover:bg-slate-50 dark:hover:bg-slate-900 border-transparent text-slate-700 dark:text-slate-300'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <Archive size={14} className={showArchived ? 'text-indigo-600' : 'text-slate-400 group-hover/item:text-indigo-550'} />
                <span className="text-[10px] font-bold uppercase tracking-wider">Archivierte</span>
              </div>
              
              {/* Premium Toggle Switch / Pill indicator */}
              <div className={`w-8 h-4 rounded-full transition-all relative ${showArchived ? 'bg-indigo-600' : 'bg-slate-350 dark:bg-slate-700'}`}>
                <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${showArchived ? 'left-4.5' : 'left-0.5'}`} />
              </div>
            </button>
          )}

          {/* Toggle 3: Zusatzinfos */}
          {setShowTaskDetails && (
            <button
              type="button"
              aria-pressed={showTaskDetails}
              aria-label="Zusatzinfos auf Aufgabenkarten anzeigen"
              onClick={() => setShowTaskDetails(!showTaskDetails)}
              title="Blendet zusätzliche Details auf Aufgabenkarten ein/aus (z. B. Untertitel, Notizen, Startzeit)"
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left transition-all duration-200 group/item border ${
                showTaskDetails 
                  ? 'hover:bg-slate-50 dark:hover:bg-slate-900 border-transparent text-slate-700 dark:text-slate-300'
                  : 'bg-indigo-50/50 border-indigo-100 dark:bg-indigo-900/20 dark:border-indigo-900/50 text-indigo-600 dark:text-indigo-400 font-bold'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <Eye size={14} className={showTaskDetails ? 'text-slate-400 group-hover/item:text-indigo-550' : 'text-indigo-600'} />
                <span className="text-[10px] font-bold uppercase tracking-wider">Zusatzinfos</span>
              </div>
              
              {/* Premium Toggle Switch / Pill indicator */}
              <div className={`w-8 h-4 rounded-full transition-all relative ${showTaskDetails ? 'bg-indigo-600' : 'bg-slate-350 dark:bg-slate-700'}`}>
                <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${showTaskDetails ? 'left-4.5' : 'left-0.5'}`} />
              </div>
            </button>
          )}

          {/* Toggle 4: Adaptive UI-Dichte (Standard vs. Experte) */}
          {effectiveSetUiMode && (
            <button
              type="button"
              data-testid="toggle-ui-mode"
              aria-pressed={effectiveUiMode === 'expert'}
              aria-label="Experten-Modus für maximale Informationsdichte umschalten"
              onClick={() => effectiveSetUiMode(effectiveUiMode === 'expert' ? 'standard' : 'expert')}
              title="Standard: Ruhiges Kartenbild, sekundäre Metadaten bei Hover · Experte: Alle Metadaten und Ansichten permanent sichtbar"
              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-left transition-all duration-200 group/item border ${
                effectiveUiMode === 'expert'
                  ? 'bg-indigo-50/50 border-indigo-100 dark:bg-indigo-900/20 dark:border-indigo-900/50 text-indigo-600 dark:text-indigo-400 font-bold'
                  : 'hover:bg-slate-50 dark:hover:bg-slate-900 border-transparent text-slate-700 dark:text-slate-300'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <Sparkles size={14} className={effectiveUiMode === 'expert' ? 'text-indigo-600' : 'text-slate-400 group-hover/item:text-indigo-550'} />
                <span className="text-[10px] font-bold uppercase tracking-wider">
                  Dichte: {effectiveUiMode === 'expert' ? 'Experte' : 'Standard'}
                </span>
              </div>

              <div className={`w-8 h-4 rounded-full transition-all relative ${effectiveUiMode === 'expert' ? 'bg-indigo-600' : 'bg-slate-350 dark:bg-slate-700'}`}>
                <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all ${effectiveUiMode === 'expert' ? 'left-4.5' : 'left-0.5'}`} />
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
